import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery } from "react-query";
import { TemplateFeedDesignBoundary } from "../TemplateFeedDesignBoundary";
import { handleNavigate } from "../../../../Utils/StayUpToDate/RenderComponent";
import { updateRefClick } from "../../../../Utils/SupabaseServices/ContentDatabase";
import { useTemplateAnalyticsOptional } from "../TemplateAnalyticsContext";
import { useTemplateFeedPostTracking } from "../useTemplateFeedPostTracking";
import { PostClickTrackingProvider } from "../../../../components/FeedPost/PostInfo/PostClickTrackingContext";
import { TemplateFeedMediaRevealRoot } from "../TemplateFeedMediaRevealRoot";
import type {
  HighlightItem,
  ThemeTokens,
  TemplateNavigateFn,
  TemplatePlatformDeepLink,
} from "../types";
import { tplThemeVars } from "../templateThemeVars";
import { mergeTemplateRefs, useTemplateStyles } from "../useTemplateStyles";
import { useTemplateTabScrollIntoView } from "../useTemplateTabScrollIntoView";
import { TemplateTabFeedPendingGrid } from "../TemplateTabFeedPendingGrid";
import {
  fetchTemplatePlatformPostsPage,
  getNextTemplatePlatformPostsPageParam,
  mapPostsForTemplateRenderIndividualUI,
  normalizeTemplatePlatformPostIds,
  PLATFORM_POSTS_PAGE_SIZE,
  templatePlatformHighlightTabs,
  templatePlatformPostsInfiniteQueryKey,
} from "../templatePlatformPostsQuery";
import { useMasterTemplateContentOptional } from "../MasterTemplateContentContext";
import { useCreatorDataLoading } from "../templateData";
import "../templateStayUpToDateEmbed.css";

const RenderIndividualUI = React.lazy(
  () =>
    import(
      "../../../../components/StayUpToDateComponent/StayUpToDateIndividuelComponent/RenderIndividualUI"
    ),
);

function PlatformTabBar({
  tabs,
  activeKey,
  onSelect,
  tabBarRef,
}: {
  tabs: HighlightItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  tabBarRef: React.RefObject<HTMLDivElement>;
}) {
  if (tabs.length <= 1) return null;
  return (
    <div
      className="mte-tpl-tabbar"
      ref={tabBarRef}
      role="tablist"
      aria-label="Latest content filters"
    >
      {tabs.map((tab) => {
        const key = tab.iconKey || tab.id;
        const active = key === activeKey;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            className={
              active
                ? "mte-tpl-tab-pill mte-tpl-tab-pill--active"
                : "mte-tpl-tab-pill"
            }
            onClick={() => onSelect(key)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

type PlatformEmbedVariant = "shop" | "content" | "platform";

/** CSS / pending-grid variants only know shop|content — platform paints like content. */
function platformVisualVariant(
  embedVariant: PlatformEmbedVariant,
): "shop" | "content" {
  return embedVariant === "shop" ? "shop" : "content";
}

interface PlatformTabFeedPanelProps {
  tab: HighlightItem;
  isActive: boolean;
  T: ThemeTokens;
  userId: string;
  username: string;
  contentShape: string;
  embedVariant: PlatformEmbedVariant;
  suppressPendingGrid?: boolean;
  userShowSocials?: unknown[] | null;
  onFeedReady?: () => void;
}

function PlatformTabFeedPanel({
  tab,
  isActive,
  T,
  userId,
  username,
  contentShape,
  embedVariant,
  suppressPendingGrid = false,
  userShowSocials,
  onFeedReady,
}: PlatformTabFeedPanelProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const batchGridRef = useRef<HTMLDivElement | null>(null);
  const templateAnalytics = useTemplateAnalyticsOptional();
  const postTracking = useTemplateFeedPostTracking(
    templateAnalytics?.pageKey || "platform",
    {
      sectionId: "platformHighlight",
      sectionType: "PlatformHighlightGrid",
      contentView: "latest",
      contentTab: tab.iconKey || tab.id,
    },
  );

  const [playEnterAnim, setPlayEnterAnim] = useState(true);
  /** Keep query warm after first visit so tab switches don't flash a skeleton. */
  const [hasActivated, setHasActivated] = useState(false);

  useEffect(() => {
    if (isActive) setHasActivated(true);
  }, [isActive]);

  const handleRedirect = useCallback(
    (
      postId: string,
      media_url?: string,
      platform?: string,
      name?: string,
      title?: string,
      isLinks?: boolean,
    ) => {
      if (!userId || !postId) return;
      const label = name || title || postId;
      if (postTracking) {
        if (isLinks === true) {
          postTracking.handleShop?.(
            postId,
            media_url || "",
            platform,
            label,
            userId,
          );
        } else if (isLinks === false) {
          postTracking.handleRedirect?.(
            postId,
            media_url || "",
            platform,
            label,
            userId,
          );
        } else {
          templateAnalytics?.trackPostClick({
            postId,
            mediaUrl: media_url,
            platform,
            name,
            title,
            sectionId: "platformHighlight",
            sectionType: "PlatformHighlightGrid",
            contentTab: tab.iconKey || tab.id,
          });
          void updateRefClick(postId, userId);
        }
        return;
      }
      if (userId && postId) {
        templateAnalytics?.trackPostClick({
          postId,
          mediaUrl: media_url,
          platform,
          name,
          title,
          sectionId: "platformHighlight",
          sectionType: "PlatformHighlightGrid",
          contentTab: tab.iconKey || tab.id,
        });
        void updateRefClick(postId, userId);
      }
    },
    [userId, templateAnalytics, postTracking, tab],
  );

  const handleNextPage = useCallback(() => {}, []);
  const handlePrevPage = useCallback(() => {}, []);

  const platformKey = String(tab.iconKey || tab.id || "")
    .trim()
    .toLowerCase();
  const isYouTubeTab = platformKey === "youtube" || platformKey === "yt";
  const orderedIds = useMemo(
    () => normalizeTemplatePlatformPostIds(tab.postIds),
    [tab.postIds],
  );
  const canFetch = Boolean(
    userId && username && platformKey && orderedIds.length > 0,
  );

  const postsQuery = useInfiniteQuery(
    templatePlatformPostsInfiniteQueryKey(userId, username, platformKey),
    ({ pageParam = 0 }) =>
      fetchTemplatePlatformPostsPage({
        userId,
        username,
        platformKey,
        offset: pageParam,
        limit: PLATFORM_POSTS_PAGE_SIZE,
        userShowSocials,
      }),
    {
      enabled: canFetch && (isActive || hasActivated),
      staleTime: 60_000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      getNextPageParam: getNextTemplatePlatformPostsPageParam,
    },
  );

  const posts = useMemo(
    () => (postsQuery.data?.pages ?? []).flatMap((page) => page.posts),
    [postsQuery.data],
  );

  const postsQueryLoading = canFetch
    ? Boolean(postsQuery.isLoading) ||
      (Boolean(postsQuery.isFetching) && posts.length === 0)
    : Boolean(userId && orderedIds.length > 0);
  const isError = Boolean(postsQuery.isError);
  const hasNextPage = Boolean(postsQuery.hasNextPage);
  const isFetchingNextPage = Boolean(postsQuery.isFetchingNextPage);
  const fetchNextPage = postsQuery.fetchNextPage;

  useEffect(() => {
    if (!isActive) return;
    if (!canFetch || !postsQueryLoading) onFeedReady?.();
  }, [isActive, canFetch, postsQueryLoading, onFeedReady]);

  // Skip empty RPC pages (filtered ids) so we don't flash "No posts" mid-feed.
  useEffect(() => {
    if (!canFetch || !hasActivated) return;
    if (postsQueryLoading || isFetchingNextPage) return;
    if (posts.length > 0) return;
    if (!hasNextPage) return;
    void fetchNextPage();
  }, [
    canFetch,
    hasActivated,
    postsQueryLoading,
    isFetchingNextPage,
    posts.length,
    hasNextPage,
    fetchNextPage,
  ]);

  const syntheticComponent = useMemo(
    () => ({
      id: `template-platform-feed-${platformKey}`,
      type: "content",
      content_shape: contentShape,
      pinned_posts: "",
      // YouTube content tab: no hover mute control (thumbnails aren't muted clips).
      content_sound: !isYouTubeTab,
    }),
    [platformKey, contentShape, isYouTubeTab],
  );

  const individualRows = useMemo(() => {
    // Offset RPC returns newest-first pages; keep that order (do not re-sort by
    // the capped highlight id list from get_general_feed_count_content).
    const mapped = mapPostsForTemplateRenderIndividualUI(posts);
    const key = String(platformKey || "").trim().toLowerCase();
    const stampPlatform = key === "yt" ? "youtube" : key === "instagram_posts" || key === "instagram_stories"
      ? "instagram"
      : key;
    if (!stampPlatform) return mapped;
    return mapped.map((row) => ({
      ...row,
      platform: String(row.platform || stampPlatform).toLowerCase() === "yt"
        ? "youtube"
        : String(row.platform || stampPlatform).toLowerCase(),
    }));
  }, [posts, platformKey]);

  useEffect(() => {
    setPlayEnterAnim(true);
  }, [platformKey]);

  useEffect(() => {
    if (postsQueryLoading || individualRows.length === 0 || !playEnterAnim) return;
    const t = window.setTimeout(() => setPlayEnterAnim(false), 700);
    return () => window.clearTimeout(t);
  }, [postsQueryLoading, individualRows.length, playEnterAnim]);

  const gridStyle = useMemo(
    () =>
      ({
        ...tplThemeVars(T),
        ["--tpl-grid-gap" as string]: "clamp(10px, 2cqi, 18px)",
        ["--tpl-card-border" as string]: `${T.ink}22`,
      }) as React.CSSProperties,
    [T],
  );
  const styleGridRef = useTemplateStyles<HTMLDivElement>(gridStyle);
  const gridRef = mergeTemplateRefs(styleGridRef, batchGridRef);
  let body: React.ReactNode;
  if (isError) {
    body = (
      <div className="mte-platform-state">This section could not be loaded.</div>
    );
  } else if (postsQueryLoading) {
    body = suppressPendingGrid ? null : (
      <div
        className="mte-tpl-tab-feed-stage mte-tpl-tab-feed-stage--content"
        aria-busy="true"
      >
        <TemplateTabFeedPendingGrid
          pageVariant="content"
          device={T.device}
          T={T}
        />
      </div>
    );
  } else if (individualRows.length === 0) {
    body =
      hasNextPage || isFetchingNextPage ? (
        suppressPendingGrid ? null : (
          <div
            className="mte-tpl-tab-feed-stage mte-tpl-tab-feed-stage--content"
            aria-busy="true"
          >
            <TemplateTabFeedPendingGrid
              pageVariant="content"
              device={T.device}
              T={T}
            />
          </div>
        )
      ) : (
        <div className="mte-platform-state">No posts found for this platform.</div>
      );
  } else {
    const visualVariant = platformVisualVariant(embedVariant);
    body = (
      <>
        <div
          ref={gridRef}
          className={[
            "mte-tpl-tab-feed-stage",
            `mte-tpl-tab-feed-stage--${visualVariant}`,
            "tpl-stayuptodate-embed",
            "tpl-template-feed-grid",
            `tpl-template-feed-grid--${visualVariant}`,
            isYouTubeTab ? "tpl-template-feed-grid--youtube" : "",
            "mte-sut-embed-wrap",
            playEnterAnim ? "mte-tpl-grid-enter" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          data-platform={isYouTubeTab ? "youtube" : undefined}
        >
          <TemplateFeedDesignBoundary T={T}>
            <TemplateFeedMediaRevealRoot>
            <PostClickTrackingProvider value={postTracking || {}}>
            <Suspense
              fallback={
                <TemplateTabFeedPendingGrid
                  pageVariant="content"
                  device={T.device}
                  T={T}
                />
              }
            >
              <RenderIndividualUI
                data={individualRows}
                currentIndex={0}
                title={tab.label || ""}
                handleNavigate={handleNavigate}
                component={syntheticComponent}
                handleNextPage={handleNextPage}
                handlePrevPage={handlePrevPage}
                containerRef={containerRef}
                handleRedirect={handleRedirect}
                brand_color={T.accent}
                type="content"
                isLinks={false}
                componentType="content"
                from="website-template"
                username={username}
                user_id={userId}
                isPodcast={false}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
              />
            </Suspense>
            </PostClickTrackingProvider>
            </TemplateFeedMediaRevealRoot>
          </TemplateFeedDesignBoundary>
        </div>
        {isFetchingNextPage ? (
          <div
            className="mte-tpl-tab-feed-stage mte-tpl-tab-feed-stage--content"
            aria-busy="true"
          >
            <TemplateTabFeedPendingGrid
              pageVariant={visualVariant}
              device={T.device}
              T={T}
              maxItems={PLATFORM_POSTS_PAGE_SIZE}
            />
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div
      className={
        isActive
          ? "mte-tpl-tab-panel mte-tpl-tab-panel--active"
          : "mte-tpl-tab-panel"
      }
      role="tabpanel"
      hidden={!isActive}
      aria-hidden={!isActive}
    >
      {body}
    </div>
  );
}

export interface TemplatePlatformHighlightFeedProps {
  T: ThemeTokens;
  userId: string;
  username: string;
  highlights: HighlightItem[];
  onNavigate?: TemplateNavigateFn;
  platformDeepLink?: TemplatePlatformDeepLink | null;
  onPlatformDeepLinkConsumed?: () => void;
  contentShape?: string;
  embedVariant?: PlatformEmbedVariant;
  /** Page key of the page that hosts this feed — passed through to analytics so
   *  content-tab clicks can be traced back to their origin page (e.g. "platform"). */
  sourcePageKey?: string;
  /**
   * False while the host page layer is CSS-hidden (pre-mounted). Resets the
   * selected platform tab so returning to Recent Content starts on the first tab.
   */
  isActive?: boolean;
  /** Parent owns the boot skeleton — skip this feed’s own pending grid. */
  suppressPendingGrid?: boolean;
  /** Fires when the first active platform tab’s posts are ready (or there are no tabs). */
  onInitialFeedReady?: () => void;
}

/**
 * Platform highlight tabs + feed — same chrome as {@link ContentGrid} (no extra page shell).
 */
export function TemplatePlatformHighlightFeed({
  T,
  userId,
  username,
  highlights,
  onNavigate,
  platformDeepLink,
  onPlatformDeepLinkConsumed,
  contentShape = "square",
  embedVariant = "content",
  sourcePageKey,
  isActive = true,
  suppressPendingGrid = false,
  onInitialFeedReady,
}: TemplatePlatformHighlightFeedProps) {
  const templateContent = useMasterTemplateContentOptional();
  const templateAnalytics = useTemplateAnalyticsOptional();
  const liveCreatorLoading = useCreatorDataLoading();
  const userShowSocials = templateContent?.showSocials;
  const tabs = useMemo(() => templatePlatformHighlightTabs(highlights), [highlights]);
  const [activeKey, setActiveKey] = useState("");
  /** Optimistic pill so underline flips on click before URL/navigation catch up. */
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const tabBarRef = useRef<HTMLDivElement>(null!);
  const readySignaledRef = useRef(false);

  const paintedKey = pendingKey ?? activeKey;
  const tabKey = tabs.map((t) => t.iconKey || t.id).join("|");
  const paintedTabIndex = paintedKey
    ? tabs.findIndex((t) => (t.iconKey || t.id) === paintedKey)
    : -1;

  useTemplateTabScrollIntoView(
    tabBarRef,
    paintedTabIndex,
    `${tabKey}|${paintedKey}`,
  );

  useEffect(() => {
    // Host pages stay pre-mounted. Drop selection while hidden so a return visit
    // re-resolves to the first tab (unless a deep link re-applies).
    if (!isActive) {
      setActiveKey("");
      setPendingKey(null);
      readySignaledRef.current = false;
      return;
    }
    if (tabs.length === 0) {
      setActiveKey("");
      setPendingKey(null);
      return;
    }
    const platformKey = platformDeepLink?.platformKey?.trim();
    if (
      platformKey &&
      tabs.some((t) => (t.iconKey || t.id) === platformKey)
    ) {
      setActiveKey(platformKey);
      setPendingKey(null);
      onPlatformDeepLinkConsumed?.();
      return;
    }
    setActiveKey((current) => {
      if (current && tabs.some((t) => (t.iconKey || t.id) === current)) {
        return current;
      }
      return tabs[0].iconKey || tabs[0].id;
    });
  }, [isActive, tabKey, tabs, platformDeepLink, onPlatformDeepLinkConsumed]);

  useEffect(() => {
    if (pendingKey == null) return;
    if (pendingKey === activeKey) setPendingKey(null);
  }, [pendingKey, activeKey]);

  const handleSelectTab = useCallback(
    (key: string) => {
      const tab = tabs.find((t) => (t.iconKey || t.id) === key);
      setPendingKey(key);
      if (embedVariant === "content") {
        templateAnalytics?.trackContentTab({
          slug: key,
          label: tab?.label || key,
          contentView: "latest",
          sourcePageKey,
        });
        setActiveKey(key);
        if (onNavigate) {
          onNavigate("content", { carouselSlug: key, contentView: "latest" });
        }
      } else {
        templateAnalytics?.trackContentTab({
          slug: key,
          label: tab?.label || key,
          contentView: "latest",
          sourcePageKey: sourcePageKey || "content",
        });
        setActiveKey(key);
        if (onNavigate) {
          onNavigate("content", { carouselSlug: key, contentView: "latest" });
        }
      }
    },
    [onNavigate, tabs, templateAnalytics, embedVariant, sourcePageKey],
  );

  const usernameNorm = username.trim().toLowerCase();

  const handleActiveFeedReady = useCallback(() => {
    if (readySignaledRef.current) return;
    readySignaledRef.current = true;
    onInitialFeedReady?.();
  }, [onInitialFeedReady]);

  useEffect(() => {
    if (liveCreatorLoading) return;
    if (tabs.length === 0) {
      handleActiveFeedReady();
    }
  }, [liveCreatorLoading, tabs.length, handleActiveFeedReady]);

  const activeHighlight =
    tabs.find((t) => (t.iconKey || t.id) === paintedKey) || tabs[0];

  if (tabs.length === 0) {
    if (liveCreatorLoading || suppressPendingGrid) {
      return suppressPendingGrid ? null : (
        <div className="mte-tpl-tab-feed-stage mte-tpl-tab-feed-stage--content" aria-busy="true">
          <TemplateTabFeedPendingGrid
            pageVariant="content"
            device={T.device}
            T={T}
          />
        </div>
      );
    }
    return (
      <div className="mte-platform-state">No platform content is available yet.</div>
    );
  }

  return (
    <>
      <PlatformTabBar
        tabs={tabs}
        activeKey={paintedKey}
        onSelect={handleSelectTab}
        tabBarRef={tabBarRef}
      />
      {activeHighlight ? (
        <div className="mte-tpl-carousel-title">{activeHighlight.label}</div>
      ) : null}
      <div className="mte-tpl-tab-panels-stack">
        {tabs.map((tab) => {
          const tabKeyId = tab.iconKey || tab.id;
          return (
            <PlatformTabFeedPanel
              key={tabKeyId}
              tab={tab}
              isActive={tabKeyId === paintedKey}
              T={T}
              userId={userId}
              username={usernameNorm}
              contentShape={contentShape}
              embedVariant={embedVariant}
              suppressPendingGrid={suppressPendingGrid}
              userShowSocials={userShowSocials}
              onFeedReady={
                tabKeyId === paintedKey ? handleActiveFeedReady : undefined
              }
            />
          );
        })}
      </div>
    </>
  );
}
